import React, { useState } from "react";
import { MessageCircle, Check, Copy as CopyIcon, AlertCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * Reusable WhatsApp share dialog for school communications.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: fn
 *  - budget: { id, number }
 *  - school: { name, whatsapp_director, has_users, invite_token, first_share_done_at }
 *  - title, headlineLabel, extraMessage, includeLink
 */
export default function ShareBudgetDialog({
  open,
  onOpenChange,
  budget,
  school,
  title = "Enviar para a escola?",
  headlineLabel = "Mensagem que será enviada",
  extraMessage = "",
  includeLink = true,
}) {
  const [copiedField, setCopiedField] = useState("");

  if (!budget || !school) return null;

  const origin = window.location.origin;
  // Prefer the friendly code (school-slug-N) over the raw UUID when available.
  const code = budget.friendly_code || budget.id;
  const budgetUrl = `${origin}/orcamento/${code}`;
  const inviteUrl = school.invite_token ? `${origin}/convite/${school.invite_token}` : "";
  const needsInvite = !school.has_users && !!inviteUrl;
  const isFirstShare = !school.first_share_done_at;

  // === Composição da mensagem (padrão único — primeira vez ou não) ===
  // Formato acordado com o cliente: cabeçalho em itálico (WhatsApp), referência
  // na mesma linha do número do orçamento, link embaixo, bloco de convite
  // SOMENTE quando a escola ainda não tem diretor cadastrado.
  const refText = (budget.reference || "").trim();
  const refSuffix = refText ? ` · Referência: ${refText}` : "";
  let message = "";
  if (extraMessage) {
    message = `${extraMessage}\n\nOrçamento Nº ${budget.number}${refSuffix}:\n${includeLink ? budgetUrl + "\n" : ""}`;
  } else {
    const welcome = isFirstShare ? [
      `Olá, equipe da ${school.name || "escola"}!`,
      "",
      "Novidade da AMC Escolas:",
      "Agora temos um sistema online para gerenciar seus orçamentos e serviços de manutenção de forma simples, rápida e segura.",
      "",
      "O que você pode fazer no sistema:",
      "• Acompanhar orçamentos em tempo real",
      "• Aprovar serviços pelo WhatsApp",
      "• Consultar preços médios de serviços",
      "• Ver histórico completo da sua escola",
      "",
    ] : [];
    const lines = [
      ...welcome,
      "_Mensagem automática do sistema AMC Escolas._",
      "Segue o link do orçamento após a elaboração pela nossa equipe.",
      "",
      `Orçamento Nº ${budget.number}${refSuffix}:`,
      ...(includeLink ? [budgetUrl] : []),
    ];
    if (needsInvite) {
      lines.push(
        "",
        "Primeiro acesso? Cadastre a senha de sua escola:",
        inviteUrl,
      );
    }
    lines.push(
      "",
      "Ficamos no aguardo.",
      "_AMC Escolas_",
    );
    message = lines.join("\n");
  }

  const waDigits = String(school.whatsapp_director || "").replace(/\D/g, "");
  const normalized = waDigits.startsWith("55") ? waDigits : "55" + waDigits;
  const waUrl = waDigits
    ? `https://wa.me/${normalized}?text=${encodeURIComponent(message)}`
    : `https://wa.me/?text=${encodeURIComponent(message)}`;

  const close = () => onOpenChange(false);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(key);
      toast.success("Copiado!");
      setTimeout(() => setCopiedField(""), 1500);
    } catch {
      toast.error("Erro ao copiar");
    }
  };

  const handleSendClick = () => {
    // Marca como enviado no backend (first_share_done_at). Non-blocking.
    if (isFirstShare && school.id) {
      api.post(`/schools/${school.id}/mark-share-sent`).catch(() => {});
    }
    setTimeout(close, 500);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent
        className="max-w-md p-0 gap-0 max-h-[90vh] flex flex-col overflow-hidden"
        data-testid="share-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-emerald-600" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 min-h-0">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
              Destinatário
            </p>
            <p className="font-heading text-sm font-black text-[#1e3a8a] break-words">
              {school.name}
            </p>
            {school.whatsapp_director ? (
              <p className="text-xs text-slate-600 mt-0.5">
                WhatsApp: {school.whatsapp_director}
              </p>
            ) : (
              <p className="text-xs text-amber-700 mt-0.5">
                ⚠ Esta escola não tem WhatsApp cadastrado. Você poderá colar a mensagem manualmente.
              </p>
            )}
          </div>

          {isFirstShare && (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-300 rounded-xl p-3 flex items-start gap-2" data-testid="share-first-badge">
              <Sparkles className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 text-xs text-amber-900">
                <p className="font-bold">1º contato — mensagem de boas-vindas</p>
                <p className="text-[11px] mt-0.5">Esta é a primeira vez que a escola recebe uma mensagem pelo sistema. Será enviada uma apresentação do sistema com link do orçamento e do cadastro.</p>
              </div>
            </div>
          )}

          <div className={`${isFirstShare ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"} border rounded-xl p-3`}>
            <p className={`text-[11px] uppercase tracking-wider font-semibold mb-1 ${isFirstShare ? "text-amber-700" : "text-emerald-700"}`}>
              {isFirstShare ? "Mensagem de boas-vindas" : headlineLabel}
            </p>
            <div
              className="text-xs text-slate-800 whitespace-pre-wrap break-all leading-relaxed font-sans"
              data-testid="share-message-preview"
            >
              {message}
            </div>
          </div>

          {!isFirstShare && (
            needsInvite ? (
              <div className="text-xs text-slate-500 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <span>Esta escola ainda não cadastrou acesso — o link de cadastro será incluído.</span>
              </div>
            ) : (
              <div className="text-xs text-slate-500 flex items-start gap-2">
                <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <span>Esta escola já tem acesso ao sistema.</span>
              </div>
            )
          )}

          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              onClick={() => copy(budgetUrl, "budget")}
              className="justify-start text-[11px] px-2 h-9 min-w-0"
              data-testid="btn-copy-budget-link"
            >
              {copiedField === "budget" ? <Check className="w-3 h-3 mr-1 shrink-0" /> : <CopyIcon className="w-3 h-3 mr-1 shrink-0" />}
              <span className="truncate">Copiar link</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => copy(message, "msg")}
              className="justify-start text-[11px] px-2 h-9 min-w-0"
              data-testid="btn-copy-message"
            >
              {copiedField === "msg" ? <Check className="w-3 h-3 mr-1 shrink-0" /> : <CopyIcon className="w-3 h-3 mr-1 shrink-0" />}
              <span className="truncate">Copiar mensagem</span>
            </Button>
          </div>
        </div>

        <DialogFooter className="gap-2 flex-row px-5 py-3 border-t border-slate-100 bg-white shrink-0 sm:justify-stretch">
          <Button variant="outline" onClick={close} className="flex-1" data-testid="btn-share-later">
            Agora não
          </Button>
          <a
            href={waUrl}
            target="_blank"
            rel="noreferrer"
            onClick={handleSendClick}
            className="flex-1"
            data-testid="btn-share-whatsapp"
          >
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
              <MessageCircle className="w-4 h-4 mr-2" />
              Enviar via WhatsApp
            </Button>
          </a>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
